import React, { useState } from 'react';
import {
  FaCheckCircle,
  FaFolderOpen,
  FaHourglassHalf,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../authConfig';
import { getValidToken } from '../../utils/msalTokenUtil';
import './ActionItemStatusDropdown.css';

const STATUS_CONFIGS = {
  Completed: {
    label: 'Completed',
    wrapperClass: 'wrapper-completed',
    selectClass: 'select-completed',
    iconColor: '#16a34a',
    Icon: FaCheckCircle,
  },
  'In Progress': {
    label: 'In Progress',
    wrapperClass: 'wrapper-in-progress',
    selectClass: 'select-in-progress',
    iconColor: '#d97706',
    Icon: FaHourglassHalf,
  },
  Open: {
    label: 'Open',
    wrapperClass: 'wrapper-open',
    selectClass: 'select-open',
    iconColor: '#2563eb',
    Icon: FaFolderOpen,
  },
};

const ActionItemStatusDropdown = ({
  item,
  onStatusChange,
  account,
  msalInstance,
  disabled = false,
  className = '',
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const currentStatus = item?.status || 'Open';
  const config = STATUS_CONFIGS[currentStatus] || STATUS_CONFIGS.Open;
  const CurrentIcon = config.Icon;

  const handleSelectStatus = async (newStatus) => {
    if (disabled || isUpdating) return;
    if (newStatus === currentStatus) return;

    setIsUpdating(true);

    try {
      const accessToken = await getValidToken(msalInstance, account);
      const userEmail = (account?.username || account?.name || '').trim().toLowerCase();

      // First attempt: POST to activities endpoint to automatically log activity audit & timestamp
      const payload = {
        status: newStatus,
        updatedBy: userEmail || 'User',
      };

      let response = await fetch(getApiUrl(`/v1/action-items/${item.id}/activities`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      let updatedItem = null;

      if (response.ok) {
        const resData = await response.json().catch(() => ({}));
        updatedItem = resData?.item || { ...item, status: newStatus };
      } else {
        // Fallback: PUT to direct action item update
        const putPayload = {
          customerType: item.customer_type,
          customerId: item.customer_id,
          dueDate: item.due_date,
          title: item.title || '',
          comments: item.comments || '',
          assignedTo: item.assigned_to || '',
          status: newStatus,
        };

        response = await fetch(getApiUrl(`/v1/action-items/${item.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(putPayload),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || 'Failed to update status.');
        }

        updatedItem = { ...item, status: newStatus };
      }

      toast.success(`Status updated to "${newStatus}"`);
      if (onStatusChange) {
        onStatusChange(updatedItem, newStatus);
      }
    } catch (error) {
      console.error('Error updating action item status:', error);
      toast.error(error.message || 'Could not update status.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      className={`action-item-status-wrapper ${config.wrapperClass} ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="status-select-icon">
        {isUpdating ? (
          <span
            className="spinner-border spinner-border-sm status-dropdown-spinner"
            role="status"
            aria-hidden="true"
          />
        ) : (
          <CurrentIcon size={11} style={{ color: config.iconColor }} />
        )}
      </span>

      <select
        className={`form-select form-select-sm action-item-status-select ${config.selectClass}`}
        value={currentStatus}
        onChange={(e) => handleSelectStatus(e.target.value)}
        disabled={disabled || isUpdating}
        title="Click to update status"
        aria-label="Action item status dropdown"
      >
        <option value="Open">Open</option>
        <option value="In Progress">In Progress</option>
        <option value="Completed">Completed</option>
      </select>
    </div>
  );
};

export default ActionItemStatusDropdown;
